import type {
  ParsedPreset,
  PresetType,
  OpenAIPresetPrompt,
  OpenAIPresetOrder,
  InstructPresetData,
  ContextPresetData,
  SyspromptPresetData,
} from '../types'

const MARKER_IDENTIFIERS = new Set([
  'chatHistory',
  'dialogueExamples',
  'charDescription',
  'charPersonality',
  'scenario',
  'personaDescription',
  'worldInfoBefore',
  'worldInfoAfter',
])

const KNOWN_PROMPT_IDENTIFIERS = new Set([
  'main',
  'nsfw',
  'jailbreak',
  'enhanceDefinitions',
  'chatHistory',
  'dialogueExamples',
  'charDescription',
  'charPersonality',
  'scenario',
  'personaDescription',
  'worldInfoBefore',
  'worldInfoAfter',
])

export function detectPresetType(json: any): PresetType {
  if (!json || typeof json !== 'object') return 'unknown'

  if (Array.isArray(json.prompts) && Array.isArray(json.prompt_order)) {
    return 'openai'
  }

  if (typeof json.story_string === 'string') {
    return 'context'
  }

  if (
    typeof json.input_sequence === 'string' ||
    typeof json.output_sequence === 'string' ||
    typeof json.system_sequence === 'string'
  ) {
    return 'instruct'
  }

  if (typeof json.content === 'string' && typeof json.name === 'string') {
    return 'sysprompt'
  }

  return 'unknown'
}

function parseOpenAIPrompts(prompts: any[]): OpenAIPresetPrompt[] {
  if (!Array.isArray(prompts)) return []

  return prompts.map((p) => ({
    identifier: p.identifier ?? '',
    name: p.name ?? '',
    role: p.role ?? 'system',
    content: p.content ?? '',
    system_prompt: p.system_prompt ?? false,
    marker: p.marker ?? false,
    enabled: p.enabled,
    injection_position: p.injection_position,
    injection_depth: p.injection_depth,
    injection_order: p.injection_order,
    forbid_overrides: p.forbid_overrides,
  }))
}

function parsePromptOrder(promptOrder: any[]): OpenAIPresetOrder[] {
  if (!Array.isArray(promptOrder) || promptOrder.length === 0) return []

  const firstEntry = promptOrder[0]
  if (firstEntry?.order && Array.isArray(firstEntry.order)) {
    return firstEntry.order.map((o: any) => ({
      identifier: o.identifier ?? '',
      enabled: o.enabled ?? true,
    }))
  }

  return promptOrder.map((o: any) => ({
    identifier: o.identifier ?? '',
    enabled: o.enabled ?? true,
  }))
}

function parseInstruct(json: any): InstructPresetData {
  return {
    name: json.name ?? '',
    input_sequence: json.input_sequence ?? '',
    output_sequence: json.output_sequence ?? '',
    system_sequence: json.system_sequence ?? '',
    stop_sequence: json.stop_sequence ?? '',
    first_input_sequence: json.first_input_sequence ?? '',
    last_input_sequence: json.last_input_sequence ?? '',
    first_output_sequence: json.first_output_sequence ?? '',
    last_output_sequence: json.last_output_sequence ?? '',
    last_system_sequence: json.last_system_sequence ?? '',
    input_suffix: json.input_suffix ?? '',
    output_suffix: json.output_suffix ?? '',
    system_suffix: json.system_suffix ?? '',
    wrap: json.wrap ?? false,
    macro: json.macro ?? true,
    names_behavior: json.names_behavior ?? 'none',
    system_same_as_user: json.system_same_as_user ?? false,
    skip_examples: json.skip_examples ?? false,
    story_string_prefix: json.story_string_prefix ?? '',
    story_string_suffix: json.story_string_suffix ?? '',
    sequences_as_stop_strings: json.sequences_as_stop_strings ?? true,
  }
}

function parseContext(json: any): ContextPresetData {
  return {
    name: json.name ?? '',
    story_string: json.story_string ?? '',
    example_separator: json.example_separator ?? '***',
    chat_start: json.chat_start ?? '***',
    story_string_position: json.story_string_position ?? 0,
    story_string_depth: json.story_string_depth ?? 1,
    story_string_role: json.story_string_role ?? 0,
    always_force_name2: json.always_force_name2 ?? true,
    names_as_stop_strings: json.names_as_stop_strings ?? false,
  }
}

function parseSysprompt(json: any): SyspromptPresetData {
  return {
    name: json.name ?? '',
    content: json.content ?? '',
    post_history: json.post_history ?? '',
  }
}

export function parsePreset(json: any): ParsedPreset {
  if (!json || typeof json !== 'object') {
    throw new Error('无效的预设数据')
  }

  const presetType = detectPresetType(json)

  const base: ParsedPreset = {
    preset_type: presetType,
    name: json.name ?? '',
    description: json.description ?? '',
    temperature: json.temperature ?? 0.7,
    top_p: json.top_p ?? 1,
    top_k: json.top_k ?? 0,
    repetition_penalty: json.repetition_penalty ?? 1,
    min_p: json.min_p ?? 0,
    presence_penalty: json.presence_penalty ?? 0,
    frequency_penalty: json.frequency_penalty ?? 0,
    max_tokens: json.openai_max_tokens ?? json.max_tokens ?? 300,
    context_length: json.openai_max_context ?? json.context_length ?? 4096,
    stop_sequences: Array.isArray(json.stop_sequences) ? json.stop_sequences : [],
    prompts: [],
    prompt_order: [],
    use_world_info: json.use_world_info ?? true,
    use_examples: json.use_examples ?? true,
  }

  switch (presetType) {
    case 'openai':
      base.prompts = parseOpenAIPrompts(json.prompts)
      base.prompt_order = parsePromptOrder(json.prompt_order)
      base.name = base.name || json.openai_model || 'OpenAI Preset'
      break
    case 'instruct':
      base.instruct = parseInstruct(json)
      base.name = base.name || base.instruct.name || 'Instruct Preset'
      break
    case 'context':
      base.context = parseContext(json)
      base.name = base.name || base.context.name || 'Context Preset'
      break
    case 'sysprompt':
      base.sysprompt = parseSysprompt(json)
      base.name = base.name || base.sysprompt.name || 'System Prompt'
      break
  }

  return base
}

function getOrderedPrompts(preset: ParsedPreset): OpenAIPresetPrompt[] {
  if (preset.prompt_order.length > 0) {
    const promptMap = new Map<string, OpenAIPresetPrompt>()
    for (const p of preset.prompts) {
      promptMap.set(p.identifier, p)
    }

    const ordered: OpenAIPresetPrompt[] = []
    for (const orderEntry of preset.prompt_order) {
      const prompt = promptMap.get(orderEntry.identifier)
      if (prompt) {
        ordered.push({ ...prompt, enabled: orderEntry.enabled })
      } else if (MARKER_IDENTIFIERS.has(orderEntry.identifier)) {
        ordered.push({
          identifier: orderEntry.identifier,
          name: orderEntry.identifier,
          marker: true,
          enabled: orderEntry.enabled,
        })
      }
    }

    for (const prompt of preset.prompts) {
      if (!ordered.some((o) => o.identifier === prompt.identifier)) {
        ordered.push(prompt)
      }
    }

    return ordered
  }

  return preset.prompts
}

export function generatePresetSystemPrompt(preset: ParsedPreset): string {
  switch (preset.preset_type) {
    case 'openai':
      return generateOpenAIPrompt(preset)
    case 'sysprompt':
      return generateSyspromptText(preset)
    case 'instruct':
      return generateInstructText(preset)
    case 'context':
      return generateContextText(preset)
    default:
      return ''
  }
}

function generateOpenAIPrompt(preset: ParsedPreset): string {
  const ordered = getOrderedPrompts(preset)
  const parts: string[] = []

  for (const prompt of ordered) {
    if (prompt.marker) continue
    if (prompt.enabled === false) continue
    if (!prompt.content?.trim()) continue

    parts.push(prompt.content.trim())
  }

  return parts.join('\n\n')
}

function generateSyspromptText(preset: ParsedPreset): string {
  if (!preset.sysprompt) return ''
  const parts: string[] = []
  if (preset.sysprompt.content) parts.push(preset.sysprompt.content)
  if (preset.sysprompt.post_history) parts.push(preset.sysprompt.post_history)
  return parts.join('\n\n')
}

function generateInstructText(preset: ParsedPreset): string {
  if (!preset.instruct) return ''
  const inst = preset.instruct
  const parts: string[] = []

  parts.push(`[Instruct Template: ${inst.name || 'Unnamed'}]`)
  parts.push('')

  if (inst.system_sequence) {
    parts.push(`System sequence: ${inst.system_sequence}`)
  }
  if (inst.input_sequence) {
    parts.push(`User sequence: ${inst.input_sequence}`)
  }
  if (inst.output_sequence) {
    parts.push(`Assistant sequence: ${inst.output_sequence}`)
  }
  if (inst.stop_sequence) {
    parts.push(`Stop sequence: ${inst.stop_sequence}`)
  }

  return parts.join('\n')
}

function generateContextText(preset: ParsedPreset): string {
  if (!preset.context) return ''
  return preset.context.story_string || ''
}

export function getPromptsForDisplay(preset: ParsedPreset): Array<{
  identifier: string
  name: string
  content: string
  role: string
  isMarker: boolean
  isEnabled: boolean
  isKnown: boolean
}> {
  const ordered = getOrderedPrompts(preset)

  return ordered.map((p) => ({
    identifier: p.identifier,
    name: p.name || p.identifier,
    content: p.content ?? '',
    role: p.role ?? 'system',
    isMarker: p.marker ?? false,
    isEnabled: p.enabled !== false,
    isKnown: KNOWN_PROMPT_IDENTIFIERS.has(p.identifier),
  }))
}
